// DonationsFeed — recent-donations feed for the v2 Overview (CHG-1238).
//
// Vertical list (NOT a DataTable — a feed conveys different importance than
// a table) with avatar / donor / designation / amount / relative time. Footer
// link to /donations for the full list.
//
// Per .forge/plans/v2-overview.md.

import { Link } from "react-router-dom";
import { useFormatters } from "../../contexts/IntlContext";
import type { OverviewRecentDonation } from "../../api/overview";

export interface DonationsFeedProps {
  donations: OverviewRecentDonation[];
  loading?: boolean;
  /** Footer "View all" link target. Defaults to /donations. */
  viewAllRoute?: string;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "··";
}

export function DonationsFeed({
  donations,
  loading,
  viewAllRoute = "/donations",
}: DonationsFeedProps) {
  const fmt = useFormatters();

  return (
    <section
      aria-labelledby="donations-feed-title"
      className="card"
      style={{
        padding: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "14px 16px",
          borderBottom: "1px solid var(--color-border-subtle)",
        }}
      >
        <h2
          id="donations-feed-title"
          style={{ fontSize: 14, fontWeight: 500, margin: 0, color: "var(--color-text)" }}
        >
          Recent donations
        </h2>
      </header>

      {loading ? (
        <ul role="list" style={{ listStyle: "none", margin: 0, padding: "8px 0" }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <li
              key={i}
              role="listitem"
              data-testid="donations-feed-skeleton-row"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 16px",
                opacity: 0.5,
              }}
            >
              <div style={{ width: 32, height: 32, borderRadius: 9999, background: "var(--color-bg-subtle)" }} />
              <div style={{ flex: 1, height: 12, background: "var(--color-bg-subtle)", borderRadius: 4 }} />
              <div style={{ width: 60, height: 12, background: "var(--color-bg-subtle)", borderRadius: 4 }} />
            </li>
          ))}
        </ul>
      ) : donations.length === 0 ? (
        <div
          style={{
            padding: "32px 16px",
            textAlign: "center",
            color: "var(--color-text-muted)",
            fontSize: 13,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-secondary)", marginBottom: 4 }}>
            No donations yet
          </div>
          <div>Share your donate page to start receiving gifts.</div>
        </div>
      ) : (
        <ul role="list" style={{ listStyle: "none", margin: 0, padding: "4px 0" }}>
          {donations.map((d) => (
            <li
              key={d.id}
              role="listitem"
              data-donation-id={d.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 16px",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: 9999,
                  background: "var(--color-primary-tint)",
                  color: "var(--color-primary)",
                  fontSize: 11,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {initials(d.donor_name)}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "var(--color-text)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {d.donor_name}
                </div>
                <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
                  {d.designation_name}
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div
                  className="tabular"
                  style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text)" }}
                >
                  {fmt.formatCurrency(d.amount_minor_units)}
                </div>
                <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>
                  {fmt.formatDate(d.created_at, "relative")}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <footer
        style={{
          padding: "10px 16px",
          borderTop: "1px solid var(--color-border-subtle)",
          textAlign: "right",
        }}
      >
        <Link
          to={viewAllRoute}
          style={{
            fontSize: 12,
            color: "var(--color-primary)",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          View all donations →
        </Link>
      </footer>
    </section>
  );
}
